import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import { AuthApi } from "../../api/features/auth/ops";
import type {
  AuthResponse,
  LoginInput,
  RefreshSessionResult,
  SignupInput,
} from "@/dashboard-api-types/api/auth.types";

export type AuthUser = AuthResponse["user"];

// The User Context
const UserContext = createContext<UserContextValue | undefined>(undefined);
UserContext.displayName = "UserContext";

export interface UserContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;

  signup: (input: SignupInput) => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;

  setSession: (data: AuthResponse | null) => void;
  clearSession: () => void;
}

const UserContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  // State to hold user data
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return sessionStorage.getItem("accessToken");
  });
  const [isInitializing, setIsInitializing] = useState(true);

  const setSession = useCallback((data: AuthResponse | null) => {
    if (!data?.user || !data?.accessToken) {
      setUser(null);
      setAccessToken(null);
      sessionStorage.removeItem("accessToken");
      return;
    }

    setUser(data.user);
    setAccessToken(data.accessToken);
    sessionStorage.setItem("accessToken", data.accessToken);
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    sessionStorage.removeItem("accessToken");
  }, [setSession]);

  const signup = useCallback(
    async (input: SignupInput) => {
      await AuthApi.signupAsk(input);
    },
    [setSession]
  );

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await AuthApi.login(input);
      setSession(result);
    },
    [setSession]
  );

  const logout = useCallback(async () => {
    try {
      await AuthApi.logout();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshSession = useCallback(async () => {
    try {
      const result = await AuthApi.refreshSession();
      setSession(result);
    } catch {
      clearSession();
    }
  }, [setSession, clearSession]);

  useEffect(() => {
    (async () => {
      try {
        await refreshSession();
      } finally {
        setIsInitializing(false);
      }
    })();
  }, [refreshSession]);

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: Boolean(user && accessToken),
      isInitializing,
      signup,
      login,
      logout,
      refreshSession,
      setSession,
      clearSession,
    }),
    [
      user,
      accessToken,
      isInitializing,
      signup,
      login,
      logout,
      refreshSession,
      setSession,
      clearSession,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook for using the UserContext
export const useUserContext = (): UserContextValue => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }

  return context;
};
// Exporting the UserContextProvider
export default UserContextProvider;
